"use client";

import { useRouter, usePathname } from "next/navigation";
import type { AiProfile } from "@/types/ai";
import { FolderKanban, Settings, Wand2 } from "lucide-react";

interface Props {
  profile: AiProfile;
}

/**
 * Ultra Navigation Bar - Navigation Only Version
 * 
 * For pages that don't need input (like Settings, Builder dashboard).
 * Shows only the resting state with Builder, CTA, and Settings.
 */
export default function UltraNavigationBarNavOnly({ profile }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  
  // Get user's custom theme colors
  const primaryColor = profile.brand?.primaryColor || "#87CEEB";
  const secondaryColor = profile.brand?.secondaryColor || "#B0E0E6";
  
  // Get Ultra Nav config
  const navConfig = profile.brand?.ultraNavConfig || {};
  const config = {
    enabled: navConfig.enabled !== false,
    showBuilder: navConfig.showBuilder !== false,
    showSettings: navConfig.showSettings !== false,
    auraEnabled: navConfig.auraEnabled !== false,
    auraIntensity: navConfig.auraIntensity ?? 0.5,
    restingStateCTA: navConfig.restingStateCTA || profile.name || "Build Your Dream",
    position: navConfig.position || "bottom",
  };
  
  if (!config.enabled) {
    return null;
  }

  const auraDuration = "3s";

  return (
    <div className={`fixed ${config.position === "top" ? "top-0" : "bottom-0"} left-0 right-0 z-50 ${config.position === "bottom" ? "pb-safe" : "pt-safe"}`}>
      {/* Ultra Aura - if enabled */}
      {config.auraEnabled && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at center, ${primaryColor}${Math.round(50 * config.auraIntensity)} 0%, ${secondaryColor}${Math.round(40 * config.auraIntensity)} 40%, transparent 70%)`,
              animation: `auraPulse ${auraDuration} ease-in-out infinite`,
              filter: "blur(50px)",
              WebkitFilter: "blur(50px)",
              transform: config.position === "top" ? "translateY(-25%)" : "translateY(25%)",
            }}
          />
        </div>
      )}
      
      {/* Main Bar Container */}
      <div className="relative mx-auto mb-4 w-auto">
        {/* Old rotating glow ring removed - replaced with Liquid Glass effect */}

        {/* Resting State - Compact Navigation */}
        <div
          className="flex items-center gap-3 px-5 py-3.5 rounded-full border-2 transition-all duration-500 group"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}20 0%, ${secondaryColor}20 100%)`,
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            borderColor: `${primaryColor}40`,
            borderImage: `linear-gradient(135deg, ${primaryColor}40, ${secondaryColor}40, ${primaryColor}40) 1`,
            boxShadow: `0 8px 32px ${primaryColor}30, 0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 ${primaryColor}20`,
          }}
        >
          {/* Builder Icon */}
          {config.showBuilder && (
            <button
              onClick={() => router.push("/builder")}
              className="relative p-2.5 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 flex-shrink-0 group/builder"
              style={{
                background: pathname?.startsWith("/builder") 
                  ? `linear-gradient(135deg, ${primaryColor}30, ${secondaryColor}30)`
                  : "transparent",
              }}
              title="Builder - Create your dream AI"
              aria-label="Open Builder"
            >
              <FolderKanban 
                className="w-5 h-5 transition-colors duration-300" 
                style={{ color: pathname?.startsWith("/builder") ? primaryColor : primaryColor }}
              />
              <div 
                className="absolute inset-0 rounded-full opacity-0 group-hover/builder:opacity-100 transition-opacity duration-300"
                style={{
                  background: `radial-gradient(circle, ${primaryColor}30, transparent)`,
                  filter: "blur(8px)",
                }}
              />
            </button>
          )}

          {/* Dream CTA */}
          <button
            onClick={() => router.push("/chat")}
            className="flex-1 px-6 py-2.5 text-center font-semibold transition-all duration-300 rounded-full hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group/cta"
            style={{ 
              color: profile.brand?.textColor || "#000000",
            }}
          >
            <div
              className="absolute inset-0 rounded-full opacity-0 group-hover/cta:opacity-100 transition-opacity duration-500"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 50%, ${primaryColor} 100%)`,
                backgroundSize: "200% 200%",
                animation: "dreamGradientShift 3s ease infinite",
              }}
            />
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Wand2 className="w-4 h-4" style={{ color: primaryColor }} />
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{
                backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              }}>
                {config.restingStateCTA}
              </span>
            </span>
          </button>

          {/* Settings Icon */}
          {config.showSettings && (
            <button
              onClick={() => router.push("/settings")}
              className="relative p-2.5 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 flex-shrink-0 group/settings"
              style={{
                background: pathname?.startsWith("/settings")
                  ? `linear-gradient(135deg, ${primaryColor}30, ${secondaryColor}30)`
                  : "transparent",
              }}
              title="Settings - Customize your dream"
              aria-label="Open Settings"
            >
              <Settings 
                className="w-5 h-5 transition-colors duration-300" 
                style={{ color: pathname?.startsWith("/settings") ? primaryColor : primaryColor }}
              />
              <div 
                className="absolute inset-0 rounded-full opacity-0 group-hover/settings:opacity-100 transition-opacity duration-300"
                style={{
                  background: `radial-gradient(circle, ${primaryColor}30, transparent)`,
                  filter: "blur(8px)",
                }}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
